import apiClient from './api';
import authService from './auth.service';

// Activity Types from API documentation
export type ActivityType = 
  | 'USER_LOGIN_SUCCESS'
  | 'USER_LOGIN_FAILED'
  | 'USER_LOGOUT'
  | 'TOKEN_REFRESHED'
  | 'PROFILE_UPDATED'
  | 'TOKENS_REVOKED'
  | 'PASSWORD_CHANGED'
  | 'PASSWORD_RESET_REQUESTED'
  | 'PASSWORD_RESET_COMPLETED';

export type ActivityStatus = 'success' | 'failed' | 'suspicious';

export interface UserActivityLogItem {
  id: string;
  activity_type: ActivityType;
  timestamp: string; // ISO 8601 format
  ip_address?: string;
  user_agent?: string;
  device_info?: string;
  location?: string;
  status?: ActivityStatus;
  description?: string;
}

export interface PaginationMetaData {
  total_records: number;
  current_page: number;
  records_per_page: number;
}

export interface ActivityApiResponse {
  success: boolean;
  code: number;
  description: string;
  data: UserActivityLogItem[];
  meta_data: PaginationMetaData;
}

export interface ActivityQueryParams {
  limit?: number;
  offset?: number;
  startDate?: string; // ISO 8601 format
  endDate?: string; // ISO 8601 format
}

export interface RevokeAllTokensRequest {
  exclude_current_session?: boolean;
  reason?: string;
  token?: string;
}

export interface RevokeAllTokensResponse {
  success: boolean;
  message: string;
  revoked_tokens_count: number;
  timestamp?: string;
}

export interface ApiResponse<T> {
  success: boolean;
  code: number;
  description?: string;
  data?: T;
  meta_data?: PaginationMetaData;
  error?: {
    error_code: string;
    error_description: string;
    meta_data?: any;
  };
  timestamp?: string;
}

class ActivityService {
  /**
   * Get current user's activity log with optional filtering and pagination
   */
  async getUserActivity(params?: ActivityQueryParams): Promise<{ activities: UserActivityLogItem[]; meta_data: PaginationMetaData }> {
    try {
      const queryParams = new URLSearchParams();
      
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.offset) queryParams.append('offset', params.offset.toString());
      if (params?.startDate) queryParams.append('startDate', params.startDate);
      if (params?.endDate) queryParams.append('endDate', params.endDate);
      
      const endpoint = `/api/auth/activity${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      const response = await apiClient.get<ActivityApiResponse>(endpoint);
      
      if (response.data.success && response.data.data) {
        return {
          activities: response.data.data,
          meta_data: response.data.meta_data
        };
      }
      
      throw new Error(response.data.error?.error_description || 'Failed to fetch user activity');
    } catch (error) {
      console.error('Error fetching user activity:', error);
      throw error;
    }
  }

  /**
   * Get recent activity (last 50 activities)
   */
  async getRecentActivity(): Promise<UserActivityLogItem[]> {
    const response = await this.getUserActivity({ limit: 50, offset: 0 });
    return response.activities;
  }

  /**
   * Revoke all active tokens for security purposes
   */
  async revokeAllTokens(request?: RevokeAllTokensRequest): Promise<RevokeAllTokensResponse> {
    try {
      const response = await apiClient.post<ApiResponse<RevokeAllTokensResponse>>(
        '/api/auth/revoke-all-tokens',
        request || {}
      );
      
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      
      throw new Error(response.data.error?.error_description || 'Failed to revoke tokens');
    } catch (error) {
      console.error('Error revoking tokens:', error);
      throw error;
    }
  }

  /**
   * Revoke all tokens except current session (safer option)
   */
  async revokeOtherSessions(reason?: string): Promise<RevokeAllTokensResponse> {
    return this.revokeAllTokens({
      exclude_current_session: true,
      reason: reason || 'User requested logout from other devices',
      token: "" // Server reads token from HttpOnly cookie
    });
  }

  /**
   * Emergency logout - revoke all tokens including current session
   */
  async emergencyLogout(reason?: string): Promise<RevokeAllTokensResponse> {
    return this.revokeAllTokens({
      exclude_current_session: false,
      reason: reason || 'Emergency logout requested'
    });
  }

  /**
   * Get activity statistics for display
   */
  async getActivityStats(): Promise<{
    totalActivities: number;
    recentLogins: number;
    failedAttempts: number;
    lastLogin?: string;
  }> {
    try {
      const activities = await this.getRecentActivity();
      
      const stats = {
        totalActivities: activities.length,
        recentLogins: activities.filter(a => a.activity_type === 'USER_LOGIN_SUCCESS').length,
        failedAttempts: activities.filter(a => a.activity_type === 'USER_LOGIN_FAILED').length,
        lastLogin: activities.find(a => a.activity_type === 'USER_LOGIN_SUCCESS')?.timestamp
      };
      
      return stats;
    } catch (error) {
      console.error('Error calculating activity stats:', error);
      return {
        totalActivities: 0,
        recentLogins: 0,
        failedAttempts: 0
      };
    }
  }

  /**
   * Check for suspicious activity patterns
   */
  async checkSuspiciousActivity(): Promise<{
    hasSuspiciousActivity: boolean;
    failedLoginCount: number;
    unusualIpAddresses: string[];
    recentFailures: UserActivityLogItem[];
  }> {
    try {
      const activities = await this.getUserActivity({ limit: 100 });
      const recentFailures = activities.activities.filter(
        a => a.activity_type === 'USER_LOGIN_FAILED' || a.status === 'suspicious'
      );
      
      const ipAddresses = [...new Set(
        activities.activities
          .map(a => a.ip_address)
          .filter(ip => ip)
      )] as string[];
      
      return {
        hasSuspiciousActivity: recentFailures.length > 3,
        failedLoginCount: recentFailures.filter(a => a.activity_type === 'USER_LOGIN_FAILED').length,
        unusualIpAddresses: ipAddresses.slice(0, 5), // Show up to 5 recent IPs
        recentFailures: recentFailures.slice(0, 10) // Show up to 10 recent failures
      };
    } catch (error) {
      console.error('Error checking suspicious activity:', error);
      return {
        hasSuspiciousActivity: false,
        failedLoginCount: 0,
        unusualIpAddresses: [],
        recentFailures: []
      };
    }
  }

  /**
   * Format activity type for display
   */
  formatActivityType(activityType: ActivityType): string {
    const formatMap: Record<ActivityType, string> = {
      USER_LOGIN_SUCCESS: 'Successful Login',
      USER_LOGIN_FAILED: 'Failed Login',
      USER_LOGOUT: 'Logout',
      TOKEN_REFRESHED: 'Session Refreshed',
      PROFILE_UPDATED: 'Profile Updated',
      TOKENS_REVOKED: 'Sessions Revoked',
      PASSWORD_CHANGED: 'Password Changed',
      PASSWORD_RESET_REQUESTED: 'Password Reset Requested',
      PASSWORD_RESET_COMPLETED: 'Password Reset Completed'
    };
    
    return formatMap[activityType] || activityType;
  }

  /**
   * Get activity icon for display
   */
  getActivityIcon(activityType: ActivityType): string {
    const iconMap: Record<ActivityType, string> = {
      USER_LOGIN_SUCCESS: 'login',
      USER_LOGIN_FAILED: 'alert-triangle',
      USER_LOGOUT: 'logout',
      TOKEN_REFRESHED: 'refresh-cw',
      PROFILE_UPDATED: 'user',
      TOKENS_REVOKED: 'shield',
      PASSWORD_CHANGED: 'key',
      PASSWORD_RESET_REQUESTED: 'mail',
      PASSWORD_RESET_COMPLETED: 'check-circle'
    };
    
    return iconMap[activityType] || 'activity';
  }

  /**
   * Get activity color for display
   */
  getActivityColor(activityType: ActivityType, status?: ActivityStatus): string {
    if (status === 'failed' || status === 'suspicious') {
      return 'text-red-600';
    }
    
    const colorMap: Record<ActivityType, string> = {
      USER_LOGIN_SUCCESS: 'text-green-600',
      USER_LOGIN_FAILED: 'text-red-600',
      USER_LOGOUT: 'text-gray-600',
      TOKEN_REFRESHED: 'text-blue-600',
      PROFILE_UPDATED: 'text-purple-600',
      TOKENS_REVOKED: 'text-orange-600',
      PASSWORD_CHANGED: 'text-green-600',
      PASSWORD_RESET_REQUESTED: 'text-yellow-600',
      PASSWORD_RESET_COMPLETED: 'text-green-600'
    };
    
    return colorMap[activityType] || 'text-gray-600';
  }

  /**
   * Format relative time for activity timestamps
   */
  formatRelativeTime(timestamp: string): string {
    const now = new Date();
    const activityTime = new Date(timestamp);
    const diffMs = now.getTime() - activityTime.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes} minutes ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    if (diffDays < 7) return `${diffDays} days ago`;
    
    return activityTime.toLocaleDateString();
  }
}

export const activityService = new ActivityService();